import { useCallback } from 'react';
import { dangerouslyCreateSafeString } from '@atlassiansox/analytics';
import { useAppDispatch, useAppSelector } from '../../../stores/redux.store';
import {
  useAnalyticsAnonymousId,
  useAnalyticsClient,
} from '../../../utilities/analytics/analytics-web-client';
import { pageId } from '../constants';
import { useLoginTooManyAttemptsRecaptchaContext } from '../contexts/LoginTooManyAttemptsRecaptchaContext';
import { loginPasswordless, loginUsernamePassword } from '../../../actions/login-actions';
import { addFlag } from '../../../actions/flag-actions';
import { LoginPageMessages } from '../messages';
import { useAppendReasonViewingScreen } from './utility';
import { useMarketingConsentContext } from '../contexts/LoginMarketingConsentContext';
import { useRegisterComponent } from '../../orchestrator';

/**
 * Note we do not provide an onSuccess callback because a successful login results in an
 * immediate redirect away from IDAC, meaning the execution context is likely to be lost
 * before a callback can be invoked.
 *
 * We currently do not have a way to delay the redirect that follows submission.
 * */
type UseLoginSubmissionOptions = {
  /**
   * Callback to call if submission fails.
   * */
  onFailure?: () => void;
};

export const useLoginSubmission = ({ onFailure }: UseLoginSubmissionOptions = {}) => {
  useRegisterComponent(useLoginSubmission.name);

  const analyticsClient = useAnalyticsClient();
  const anonymousId = useAnalyticsAnonymousId();

  const isUsernameChecked = useAppSelector(state => state.login?.isUsernameChecked);
  const isCheckingUsername = useAppSelector(state => state.login?.isCheckingUsername);
  const isLoggingIn = useAppSelector(state => state.login?.isLoggingIn);
  const isLoading = isCheckingUsername || isLoggingIn;

  const dispatch = useAppDispatch();

  const appendReasonViewingScreen = useAppendReasonViewingScreen();

  const { captchaValue } = useLoginTooManyAttemptsRecaptchaContext();

  const { marketingConsent } = useMarketingConsentContext();

  const handleLogIn = useCallback(
    (username: string, password: string) => {
      if (!marketingConsent) {
        console.error('Form cannot be submitted without showing Marketing Consent');
        onFailure?.();
        return;
      }

      if (!isUsernameChecked) {
        console.error(
          'Cannot submit a username & password login attempt until check-username endpoint has been called'
        );
        onFailure?.();
        return;
      }

      analyticsClient.formSubmittedEvent(
        pageId,
        'loginForm',
        appendReasonViewingScreen({
          marketingConsent: dangerouslyCreateSafeString(marketingConsent.state),
        })
      );

      const onLoginError = (code: string) => {
        onFailure?.();
        analyticsClient.trackingEvent({
          page: pageId,
          action: 'submitted',
          subject: 'credentials',
          attributes: {
            successfulAuthentication: false,
            errorCode: code,
          },
        });
      };

      dispatch(
        loginUsernamePassword({
          username,
          password,
          captchaValue,
          location: window.location,
          onLoginError,
          marketingConsent,
          anonymousId,
        })
      );
    },
    [
      analyticsClient,
      anonymousId,
      appendReasonViewingScreen,
      captchaValue,
      dispatch,
      isUsernameChecked,
      marketingConsent,
      onFailure,
    ]
  );

  const handlePasswordlessLogIn = useCallback(
    async (email: string) => {
      const { success } = await dispatch(
        loginPasswordless({ email, captchaValue, location: window.location })
      );

      if (success) {
        dispatch(
          addFlag(
            'success',
            LoginPageMessages.passwordlessSuccessTitle,
            LoginPageMessages.passwordlessSuccessDescription
          )
        );
      }
    },
    [dispatch, captchaValue]
  );

  return {
    /**
     * Indicates whether username & password submission is in progress.
     * */
    isLoading,
    /**
     * Errors around the username (email) and password fields.
     * */
    onPasswordlessLogin: handlePasswordlessLogIn,
    /**
     * Handle login with username and password.
     *
     * Note: this will be a no-op if the username (email) has not been checked by calling
     * `checkUsername` before calling `onLogin`.
     *
     * For accessing the state and handlers around working with `checkUsername`, see:
     * {@link file://./useLogin.ts}.
     * */
    onLogin: handleLogIn,
  };
};
